import React, { useEffect, useState } from 'react'
import {
  Edit,
  required,
  SimpleForm,
  TextInput,
  useNotify,
  useRedirect,
  useTranslate,
  useUpdate,
  useResourceContext,
  useEditController,
} from 'react-admin'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons'
import { Grid, CircularProgress } from '@mui/material'
import { DateRangeArray } from './logic/DateRangeArray'
import { dueDateServices } from 'src/services/reservation/due-date/dueDate.service'
import dayjs from 'dayjs'

export const DueDateRangeEdit = (props, { id }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()
  const resourceName = useResourceContext()
  const { isFetching } = useEditController({ resource: resourceName, id })
  const [update] = useUpdate()
  const [loadingComponent, setLoadingComponent] = useState(false)
  const [editLoading, setEditLoading] = useState(false)

  const formatDate = (date) => {
    if (dayjs(date).isValid()) {
      return dayjs(date).format('YYYY-MM-DD')
    }
    return null
  }

  useEffect(() => {
    setLoadingComponent(true)
  }, []) 

  useEffect(() => {
    if (!isFetching){
      setTimeout(() => {
        setLoadingComponent(false)
      }, 200)
    }
  }, [isFetching]) 

  const createDateRange = async (dateRanges, due_date_range_id) => {
    const dateRangesToCreate = dateRanges?.filter((elem) => elem?.new_register)
    if (dateRangesToCreate?.length > 0) {
      try {
        const promises = dateRangesToCreate.map(async (elem) => {
          // Validar y formatear fechas antes de enviar
          elem.date_from = formatDate(elem.date_from)
          elem.date_to = formatDate(elem.date_to)
          elem.date_final = formatDate(elem.date_final)
          elem.due_date_range_id = due_date_range_id
          delete elem._id
          delete elem.new_register
          const response = await dueDateServices.createDateRange(elem)
          return response
        })
        const results = await Promise.all(promises)
        return results
      } catch (error) {
        notify(`ra.notification.create_error_date_range`, {
          type: 'error',
        })
        return false
      }
    }
    return true
  }

  const updateDateRange = async (dateRange) => {
    const dateRangesToUpdate = dateRange.filter((elem) => elem.id && elem.update_register)
    if (dateRangesToUpdate.length > 0) {
      try {
        const promises = dateRangesToUpdate.map(async (elem) => {
          elem.date_from = formatDate(elem.date_from)
          elem.date_to = formatDate(elem.date_to)
          elem.date_final = formatDate(elem.date_final)
          delete elem.update_register
          delete elem._id
          const response = await dueDateServices.updateDateRange(elem?.id, elem)
          return response
        })

        const results = await Promise.all(promises)
        return results
      } catch (error) {
        console.error('Error updating date ranges:', error)
        notify(`ra.notification.update_error_date_range`, {
          type: 'error',
        })
        return false
      }
    }
    return true
  }

  const onSuccess = async (data) => {
    try {
      const promises = [
        await createDateRange(data.date_range_relation, data.id),
        await updateDateRange(data.date_range_relation),
      ]
      await Promise.all(promises)
      setTimeout(() => {
        notify('ra.notification.updated_success_masculine', {
          type: 'success',
          messageArgs: { element: translate('ra.entity.due-date-range') },
        })
      }, 1000)
    } catch (error) {
    } finally {
      redirect('/due-date-range')
    }
  }

  const onError = () => {
    notify('ra.notification.updated_error_masculine', {
      type: 'error',
      messageArgs: { element: translate('ra.entity.due-date-range') },
    })
  }

  const saveEdit = async (data) =>{
    await update(resourceName, { id: data.id, data })
    await onSuccess(data)
  }

  const save = async (data) => {
    try {
      if(!editLoading){
        setEditLoading(true)
        await saveEdit(data).finally(()=>{
          setEditLoading(false)
        })
      }
    } catch (error) {
      onError()
    }
  }

  const transform = (data) => {
    let obj = {
      ...data,
    }

    if (data?.date_range_relation?.length > 0) {
      obj?.date_range_relation?.forEach((elem) => {
        elem.due_date_range_id = data.id
      })
    }
    return obj
  }

  return (
    <Edit {...props} mutationMode='pessimistic' actions={null} transform={transform}>
      <CardCommon title={translate('ra.title.update_due_date_range')}>
        <SimpleForm
          toolbar={
            <CardCrudBottomButtons
              btnSave={true}
              btnDelete={true}
              fieldsName={['short_name']}
              genderElement='masculine'
            />
          }
          onSubmit={save}
        >
          <Grid container spacing={3}>
            <EditInputsWrap label={translate('ra.field.name')} required>
              <TextInput source='name' label={false} fullWidth validate={[required()]} />
            </EditInputsWrap>
          </Grid>
          <Grid container spacing={3}>
            <EditInputsWrap label={translate('ra.field.date_ranges')} lg={10} md={10} xs={10}>
              {loadingComponent ? (
                <CircularProgress sx={{ marginRight: '5px' }} size={'10px'} />
              ) : (
                <DateRangeArray />
              )}
            </EditInputsWrap>
          </Grid>
        </SimpleForm>
      </CardCommon>
    </Edit>
  )
}
